import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getNatureOfGroups, getMasterGroupsByNature, getSubGroupsByGroupId, getApprovalStatusBank,
    getAllBankDetails, saveNewBankDetails, updateBankDetails,
} from '../../api/AccountsAPI/bankAccountAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

// ── Thunks ─────────────────────────────────────────────────────────────────────

export const fetchNatureOfGroups = createAsyncThunk('bankAccount/fetchNatureOfGroups', async (_, { rejectWithValue }) => {
    try { const res = await getNatureOfGroups(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch nature of groups')); }
});

export const fetchMasterGroups = createAsyncThunk('bankAccount/fetchMasterGroups', async (natureGroupId, { rejectWithValue }) => {
    try { const res = await getMasterGroupsByNature(natureGroupId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch groups')); }
});

export const fetchSubGroups = createAsyncThunk('bankAccount/fetchSubGroups', async (groupId, { rejectWithValue }) => {
    try { const res = await getSubGroupsByGroupId(groupId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub groups')); }
});

export const fetchApprovalStatusBank = createAsyncThunk('bankAccount/fetchApprovalStatus', async (roleId, { rejectWithValue }) => {
    try { const res = await getApprovalStatusBank('Bank Account', roleId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch allowed actions')); }
});

export const fetchBankGrid = createAsyncThunk('bankAccount/fetchGrid', async (_, { rejectWithValue }) => {
    try { const res = await getAllBankDetails(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch bank accounts')); }
});

export const submitNewBank = createAsyncThunk('bankAccount/submitNew', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveNewBankDetails(payload);
        const status = res?.Data;
        if (status !== 'Submitted' && status !== 'Submited') return rejectWithValue(status || 'Failed to save bank account');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save bank account')); }
});

export const submitUpdateBank = createAsyncThunk('bankAccount/submitUpdate', async (payload, { rejectWithValue }) => {
    try {
        const res = await updateBankDetails(payload);
        const status = res?.Data;
        if (status !== 'Updated') return rejectWithValue(status || 'Failed to update bank account');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to update bank account')); }
});

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    natureGroups: [], natureGroupsLoading: false,
    masterGroups: [], masterGroupsLoading: false,
    subGroups: [], subGroupsLoading: false,
    allowedActions: [], allowedActionsLoading: false,

    grid: [], gridLoading: false, gridError: null,

    saveLoading: false, saveResult: null, saveError: null,
    updateLoading: false, updateResult: null, updateError: null,
};

const bankAccountSlice = createSlice({
    name: 'bankAccount',
    initialState,
    reducers: {
        clearMasterGroups: (s) => { s.masterGroups = []; s.subGroups = []; },
        clearSubGroups: (s) => { s.subGroups = []; },
        clearSaveResult: (s) => { s.saveResult = null; s.saveError = null; },
        clearUpdateResult: (s) => { s.updateResult = null; s.updateError = null; },
        resetBankAccount: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNatureOfGroups.pending, (s) => { s.natureGroupsLoading = true; })
            .addCase(fetchNatureOfGroups.fulfilled, (s, a) => { s.natureGroupsLoading = false; s.natureGroups = a.payload; })
            .addCase(fetchNatureOfGroups.rejected, (s) => { s.natureGroupsLoading = false; });

        builder
            .addCase(fetchMasterGroups.pending, (s) => { s.masterGroupsLoading = true; })
            .addCase(fetchMasterGroups.fulfilled, (s, a) => { s.masterGroupsLoading = false; s.masterGroups = a.payload; })
            .addCase(fetchMasterGroups.rejected, (s) => { s.masterGroupsLoading = false; });

        builder
            .addCase(fetchSubGroups.pending, (s) => { s.subGroupsLoading = true; })
            .addCase(fetchSubGroups.fulfilled, (s, a) => { s.subGroupsLoading = false; s.subGroups = a.payload; })
            .addCase(fetchSubGroups.rejected, (s) => { s.subGroupsLoading = false; });

        builder
            .addCase(fetchApprovalStatusBank.pending, (s) => { s.allowedActionsLoading = true; })
            .addCase(fetchApprovalStatusBank.fulfilled, (s, a) => { s.allowedActionsLoading = false; s.allowedActions = a.payload; })
            .addCase(fetchApprovalStatusBank.rejected, (s) => { s.allowedActionsLoading = false; });

        builder
            .addCase(fetchBankGrid.pending, (s) => { s.gridLoading = true; s.gridError = null; })
            .addCase(fetchBankGrid.fulfilled, (s, a) => { s.gridLoading = false; s.grid = a.payload; })
            .addCase(fetchBankGrid.rejected, (s, a) => { s.gridLoading = false; s.gridError = a.payload; });

        builder
            .addCase(submitNewBank.pending, (s) => { s.saveLoading = true; s.saveError = null; s.saveResult = null; })
            .addCase(submitNewBank.fulfilled, (s, a) => { s.saveLoading = false; s.saveResult = a.payload; })
            .addCase(submitNewBank.rejected, (s, a) => { s.saveLoading = false; s.saveError = a.payload; });

        builder
            .addCase(submitUpdateBank.pending, (s) => { s.updateLoading = true; s.updateError = null; s.updateResult = null; })
            .addCase(submitUpdateBank.fulfilled, (s, a) => { s.updateLoading = false; s.updateResult = a.payload; })
            .addCase(submitUpdateBank.rejected, (s, a) => { s.updateLoading = false; s.updateError = a.payload; });
    },
});

export const {
    clearMasterGroups, clearSubGroups, clearSaveResult, clearUpdateResult, resetBankAccount,
} = bankAccountSlice.actions;

export const selectBankAccount = (s) => s.bankAccount;

export default bankAccountSlice.reducer;
